import { FC } from "react";
import { Control, useController } from "react-hook-form";
import { Button, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import DayOfMonthSelect from "@/components/dayOfMonthSelect";
import { DAY_OF_WEEK_OPTIONS, ScheduleFormValues } from "./useScheduleForm";

interface ScheduleDayPickerProps {
  control: Control<ScheduleFormValues>;
  // The currently-selected frequency. The picker renders Day of Week for
  // "weekly", Day of Month for "monthly", and nothing for "daily".
  frequency: ScheduleFormValues["frequency"];
}

/**
 * Conditional day picker rendered inside the Schedule dialog.
 *
 * Visual style mirrors the Subscriptions wizard `ScheduleEditor`
 * (`pages/subscriptions/.../editors/ScheduleEditor.tsx`):
 *   - Weekly: a row of 7 pill buttons (single-select; the backend stores
 *     a single cron weekday so multi-select would be misleading).
 *   - Monthly: the shared `DayOfMonthSelect` component, so Studio and
 *     Subscriptions render the day-of-month picker identically.
 */
const ScheduleDayPicker: FC<ScheduleDayPickerProps> = ({ control, frequency }) => {
  // Hooks must run on every render — keep them above the early returns.
  const dayOfWeek = useController({ control, name: "dayOfWeek" });

  if (frequency === "weekly") {
    const { field } = dayOfWeek;
    return (
      <FormItem>
        <FormLabel>Select a day of the week</FormLabel>
        <div className="al-grid al-grid-cols-7 al-gap-2">
          {DAY_OF_WEEK_OPTIONS.map(opt => {
            const isSelected = field.value === opt.value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={isSelected}
                onClick={() => field.onChange(opt.value)}
                className={cn(
                  "al-px-3 al-py-2 al-rounded-lg al-text-xs al-font-medium al-transition-all",
                  isSelected
                    ? "al-bg-blue-600 al-text-white hover:al-bg-blue-700"
                    : "al-bg-gray-100 al-text-gray-700 hover:al-bg-gray-200"
                )}
              >
                {opt.label.slice(0, 3)}
              </Button>
            );
          })}
        </div>
        <FormMessage />
      </FormItem>
    );
  }

  if (frequency === "monthly") {
    return (
      <FormField
        control={control}
        name="dayOfMonth"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Day of Month</FormLabel>
            <FormControl>
              <DayOfMonthSelect
                value={field.value}
                onValueChange={field.onChange}
                className="al-w-full"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return null;
};

export default ScheduleDayPicker;
